import { CommonModule } from '@angular/common'
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ElementRef, AfterViewInit, ViewChild } from '@angular/core'
import { LandingHeaderComponent } from '@app/modules/event-landing-page/components/landing-previewer/landing-header/landing-header.component'
import { ServerRepresentedEvent } from '@events/models/event.model'
import { EventService } from '@events/services/event.service'
import { Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'

/**
 * Event invitation event selection list
 */
@Component({
  selector: 'ngx-event-selection',
  templateUrl: './event-selection.component.html',
  styleUrls: ['./event-selection.component.scss'],
  standalone: true,
  imports: [CommonModule, LandingHeaderComponent]
})
export class EventSelectionComponent implements OnDestroy, OnInit, AfterViewInit {
  /**
   * Events list
   */
  @Input() events: Partial<ServerRepresentedEvent>[] = []
  @Input() shouldPaginate = false

  /**
   * Emits the selected event
   */
  @Output() eventSelected = new EventEmitter<Partial<ServerRepresentedEvent>>()
  @Output() loadNextPage = new EventEmitter<number>()

  public retrieving = false
  private _currentPage = 1
  private _destroy$ = new Subject()

  @ViewChild('eventList', { static: false }) eventList: ElementRef

  constructor(private eventService: EventService, private elementRef: ElementRef) {}

  ngOnInit() {
    this.eventService.retrieving$.pipe(takeUntil(this._destroy$)).subscribe((retrieving) => (this.retrieving = retrieving))
  }

  ngAfterViewInit() {
    if (this.eventList?.nativeElement) {
      this.eventList.nativeElement.addEventListener('scroll', this.onScroll.bind(this))
    }
  }

  ngOnDestroy() {
    if (this.eventList?.nativeElement) {
      this.eventList.nativeElement.removeEventListener('scroll', this.onScroll.bind(this))
    }
    this._destroy$.next(null)
    this._destroy$.complete()
  }

  /**
   * Loads the next set of events
   */
  private loadNext() {
    if (this.retrieving) return
    this.retrieving = true
    this._currentPage++
    this.loadNextPage.emit(this._currentPage)
  }

  /**
   * Handles the scroll event on the event list to load the next set of events
   * @param event - The scroll event
   */
  private onScroll(event: Event) {
    if (!this.shouldPaginate || this.retrieving) return
    const element = event.target as HTMLElement
    const scrollPosition = element.scrollTop + element.clientHeight
    const contentHeight = element.scrollHeight
    const threshold = 300 // pixels from bottom to trigger load
    if (scrollPosition >= contentHeight - threshold) this.loadNext()
  }
}
